import {useEffect, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';

import {Alert, Button} from '@design-system';

import {useAuth} from '../Auth/useAuth';
import EvaluationQuestion from '../Parent/Evaluation/EvaluationQuestion';
import useGetNextEvaluationQuestion from '../Queries/useGetNextEvaluationQuestion';
import {useRedirectAfterAuth} from '../Util/StoreRedirectUrlForAuth';
import useRetryFocus from '../Util/useRetryFocus';
import {getGreeting} from './Util';

const LOAD_ERROR_COPY = "We couldn't load your feedback question. Please try again.";
const LOADING_COPY = 'Loading your next question…';
const PANEL_HEADING = 'Tell us about your experience with transitions';
const PANEL_SUBHEADING =
  'This will help us to evaluate Principles into Practice and improve transitions for everyone.';
const COMPLETION_HEADING = 'Thank you for your feedback';

const ProHome = () => {
  const {authState} = useAuth();

  // If there's a stored URL when we mount, navigate there
  const {handleAuthenticatedAppBoot} = useRedirectAfterAuth();
  useEffect(handleAuthenticatedAppBoot, []);

  const userId = authState?.user?.id;
  const {data, isLoading, error, refetch} = useGetNextEvaluationQuestion(userId);
  const queryClient = useQueryClient();

  const [isRetrying, setIsRetrying] = useState(false);
  const retryGuardRef = useRef(false);
  const answeredQuestionIdRef = useRef<number | null>(null);

  // The app QueryClient keeps this query cached after Home unmounts, including
  // an answered question retained by a failed follow-up request, but the
  // answered-id record above does not survive the unmount. Dropping the entry
  // while an answer is still unconfirmed makes the next visit start from a
  // fresh load instead of re-offering that question. The key mirrors
  // useGetNextEvaluationQuestion's.
  useEffect(
    () => () => {
      if (answeredQuestionIdRef.current !== null) {
        queryClient.removeQueries({queryKey: ['evaluation-question', userId], exact: true});
      }
    },
    [queryClient, userId]
  );

  const greeting = getGreeting(authState?.user?.name);

  const hasValidQuestion = Boolean(data && typeof data === 'object' && data.id);

  // Clear the answered-id record whenever a different question arrives, so a
  // later error path only suppresses the specific question that was submitted.
  useEffect(() => {
    if (!hasValidQuestion) return;
    if (answeredQuestionIdRef.current !== null && data.id !== answeredQuestionIdRef.current) {
      answeredQuestionIdRef.current = null;
    }
  }, [data, hasValidQuestion]);

  const handleAnswered = () => {
    // Record the answered id BEFORE returning the refetch promise. A refetch
    // that fails leaves the question retained by React Query at the same id, and
    // this record is what tells the error branch to suppress it.
    if (hasValidQuestion) {
      answeredQuestionIdRef.current = data.id;
    }
    return refetch();
  };

  const handleRetry = () => {
    if (retryGuardRef.current) return;
    retryGuardRef.current = true;
    setIsRetrying(true);
    // React Query's refetch resolves rather than rejects on failure, so a plain
    // finally is enough to clear the guard on either outcome.
    void refetch().finally(() => {
      retryGuardRef.current = false;
      setIsRetrying(false);
    });
  };

  // React Query 4 clears `error` and re-enters status="loading" during a
  // refetch of a query with no cached data (e.g. after a first-load failure or
  // during a Retry). Anchoring the failure block on `isRetrying` as well keeps
  // the alert and Retry mounted across those transient reducer resets, so
  // focus never falls back to <body>.
  const showFailure = Boolean(error) || isRetrying;
  // Only suppress the retained answered question when the query is in an error
  // state; a mid-flight refetch after a successful POST keeps the shared
  // control mounted and self-guarded until it settles.
  const showQuestion =
    hasValidQuestion && !(Boolean(error) && data.id === answeredQuestionIdRef.current);
  const showLoading = isLoading && !hasValidQuestion && !showFailure;
  const showCompletion = !showFailure && !isLoading && !hasValidQuestion;
  // A failure that replaces what was on screen, a first load or the question
  // just answered, takes focus so it is read out, with Retry the next stop. One
  // beside a question still on screen takes focus only when nothing else has
  // it, so the rating keeps it. Retry stays mounted and focused through its
  // request (above).
  const {failureRef} = useRetryFocus(
    {failed: showFailure, focusFirstFailure: showQuestion ? 'when-unfocused' : true},
    handleRetry
  );

  return (
    <div data-testid="pro-home">
      <h1 className="text-2xl font-bold text-(--compass-on-surface)">{greeting}</h1>
      <hr className="my-6 border-t border-(--compass-border)" />

      <section
        data-testid="pro-home-panel"
        className="rounded-md border border-(--compass-border) bg-(--compass-surface) p-4 shadow-sm">
        {!showCompletion && (
          <>
            <h2 className="mb-2 text-2xl font-bold text-(--compass-on-surface)">{PANEL_HEADING}</h2>
            <p className="mb-4 max-w-2xl text-sm text-(--compass-on-surface)">{PANEL_SUBHEADING}</p>
          </>
        )}

        {showFailure ? (
          <Alert
            ref={failureRef}
            variant="error"
            data-testid="pro-home-error"
            action={
              <Button
                type="button"
                variant="outline"
                data-testid="pro-home-retry"
                className="min-h-11"
                aria-busy={isRetrying ? true : undefined}
                onClick={handleRetry}>
                Retry
              </Button>
            }>
            {LOAD_ERROR_COPY}
          </Alert>
        ) : null}

        {showLoading ? (
          <p
            role="status"
            data-testid="pro-home-loading"
            className="mt-4 text-sm text-(--compass-on-surface)">
            {LOADING_COPY}
          </p>
        ) : null}

        {showQuestion ? (
          <div className="mt-4">
            <EvaluationQuestion question={data} onSuccess={handleAnswered} />
          </div>
        ) : null}

        {/* A confirmation once the last question is answered. The polite region is
            mounted throughout, so the completion is announced when it arrives. */}
        <Alert
          variant="success"
          data-testid="pro-home-completion"
          className="my-0"
          title={
            showCompletion ? (
              <h2 className="mb-2 flex items-center gap-3 text-2xl font-bold">
                <span aria-hidden="true" className="text-3xl">
                  🎉
                </span>
                {COMPLETION_HEADING}
              </h2>
            ) : null
          }>
          {showCompletion ? (
            <div className="max-w-2xl space-y-3">
              <p>
                You have completed all available feedback questions. Your feedback helps us improve
                transitions for everyone.
              </p>
              <p>You can now explore the rest of the site using the navigation menu.</p>
              <p className="text-sm italic">
                New feedback questions may become available in the future.
              </p>
            </div>
          ) : null}
        </Alert>
      </section>
    </div>
  );
};

export default ProHome;
